import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface SendOtpResult {
  challengeId: string;
  expiresAt: string;
  resendAvailableAt: string;
  accountConflict: boolean;
}

export type VerifyOtpResult =
  | {
      accountConflict: true;
      verified: true;
      phoneE164: string;
      challengeId: string;
    }
  | {
      accountConflict?: false;
      token: string;
      guestParticipantId: string;
      isNewParticipant: boolean;
      expiresAt: string;
    };

export interface GuestEligibility {
  surveyId: string;
  allowGuestParticipation: boolean;
  surveyStatus: string;
  surveyName: string;
}

export async function checkGuestEligibility(surveyId: string): Promise<GuestEligibility> {
  const { data } = await axios.get(`${API_BASE}/api/surveys/${surveyId}/guest-eligibility`);
  return data;
}

export async function sendOtp(phone: string, surveyId: string, fingerprint: string): Promise<SendOtpResult> {
  const { data } = await axios.post(`${API_BASE}/api/guest/otp/send`, { phone, surveyId, fingerprint });
  return data;
}

export async function verifyOtp(
  challengeId: string,
  code: string,
  displayName?: string,
): Promise<VerifyOtpResult> {
  const { data } = await axios.post(`${API_BASE}/api/guest/otp/verify`, { challengeId, code, displayName });
  return data;
}

export async function recoverAccountEmail(challengeId: string): Promise<{ sent: boolean }> {
  const { data } = await axios.post(`${API_BASE}/api/guest/account-conflict/recover-email`, { challengeId });
  return data;
}

export async function unlinkAccountPhone(challengeId: string, displayName?: string): Promise<VerifyOtpResult> {
  const { data } = await axios.post(`${API_BASE}/api/guest/account-conflict/unlink-phone`, {
    challengeId,
    displayName,
  });
  return data;
}

export async function linkAccountToGuest(guestToken: string, cognitoToken: string): Promise<void> {
  await axios.post(
    `${API_BASE}/api/guest/account-link`,
    { cognitoToken },
    { headers: { Authorization: `Bearer ${guestToken}` } },
  );
}

export interface GuestDemographics {
  gender: 'MALE' | 'FEMALE';
  dateOfBirth: string; // YYYY-MM-DD
  educationLevel: 'FUNDAMENTAL' | 'MEDIO' | 'SUPERIOR' | 'POS_GRADUACAO' | 'MESTRADO' | 'DOUTORADO';
  economicLevel: 'NO_INCOME' | 'UP_TO_HALF_MW' | 'ONE_TO_TWO_MW' | 'TWO_TO_FIVE_MW' | 'FIVE_TO_TEN_MW' | 'TEN_TO_TWENTY_MW' | 'ABOVE_TWENTY_MW';
  cityId: string;
  neighborhood: string;
  displayName?: string;
}

export async function saveGuestDemographics(guestToken: string, demographics: GuestDemographics): Promise<void> {
  await axios.post(
    `${API_BASE}/api/guest/demographics`,
    demographics,
    { headers: { Authorization: `Bearer ${guestToken}` } },
  );
}

export interface GuestProfile {
  displayName: string | null;
  gender: GuestDemographics['gender'] | null;
  dateOfBirth: string | null;
  educationLevel: GuestDemographics['educationLevel'] | null;
  economicLevel: GuestDemographics['economicLevel'] | null;
  cityId: string | null;
  cityName: string | null;
  stateId: string | null;
  stateName: string | null;
  neighborhood: string | null;
  hasCompletedProfile: boolean;
}

export async function getGuestProfile(guestToken: string): Promise<GuestProfile> {
  const { data } = await axios.get(`${API_BASE}/api/guest/profile`, {
    headers: { Authorization: `Bearer ${guestToken}` },
  });
  return data;
}
